import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { actresses } from "@/mocks/actresses";
import { works } from "@/mocks/works";

interface SearchBoxProps {
  autoFocus?: boolean;
  large?: boolean;
  onNavigate?: () => void;
}

export default function SearchBox({ autoFocus = false, large = false, onNavigate }: SearchBoxProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const keyword = query.trim().toLowerCase();

  const actressResults = useMemo(() => {
    if (!keyword) return [];
    return actresses
      .filter((item) =>
        [item.name, item.nameRomaji, item.nameKana].join(" ").toLowerCase().includes(keyword),
      )
      .slice(0, 4);
  }, [keyword]);

  const workResults = useMemo(() => {
    if (!keyword) return [];
    return works
      .filter((item) => [item.title, item.titleJp, item.director].join(" ").toLowerCase().includes(keyword))
      .slice(0, 4);
  }, [keyword]);

  const hasResults = actressResults.length + workResults.length > 0;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const go = (href: string) => {
    setOpen(false);
    setQuery("");
    navigate(href);
    onNavigate?.();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (event.key === "Enter" && keyword) {
      event.preventDefault();
      if (actressResults.length > 0) {
        go(`/detail/actress/${actressResults[0].id}`);
      } else if (workResults.length > 0) {
        go(`/detail/work/${workResults[0].id}`);
      } else {
        go("/category");
      }
    }
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      <div
        className={`flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-3 transition-colors focus-within:border-primary-400 ${
          large ? "h-12 px-4" : "h-10"
        }`}
      >
        <span className={`flex items-center justify-center text-foreground-400 ${large ? "h-5 w-5" : "h-4 w-4"}`}>
          <i className="ri-search-line text-base" aria-hidden="true"></i>
        </span>
        <input
          type="text"
          value={query}
          autoFocus={autoFocus}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="搜索女优姓名或作品名称"
          aria-label="搜索女优或作品"
          className={`w-full bg-transparent font-label text-foreground-900 outline-none placeholder:text-foreground-400 ${
            large ? "text-sm" : "text-xs"
          }`}
        />
        {query ? (
          <button
            type="button"
            aria-label="清空搜索"
            onClick={() => {
              setQuery("");
              setOpen(false);
            }}
            className="flex h-5 w-5 cursor-pointer items-center justify-center text-foreground-400 transition-colors hover:text-primary-600"
          >
            <i className="ri-close-line" aria-hidden="true"></i>
          </button>
        ) : null}
      </div>

      {open && keyword ? (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[420px] overflow-y-auto rounded-lg border border-background-200 bg-background-50 p-2">
          {hasResults ? (
            <>
              {actressResults.length > 0 ? (
                <div className="mb-1">
                  <p className="px-2 py-1.5 font-label text-[11px] tracking-wide text-foreground-400">
                    女优档案
                  </p>
                  {actressResults.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => go(`/detail/actress/${item.id}`)}
                      className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-left transition-colors hover:bg-primary-50"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                        <i className="ri-user-line text-sm" aria-hidden="true"></i>
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-label text-sm text-foreground-900">{item.name}</span>
                        <span className="block truncate font-label text-[11px] text-foreground-500">
                          {item.nameRomaji} · {item.agency}
                        </span>
                      </span>
                      <i className="ri-arrow-right-s-line text-foreground-300" aria-hidden="true"></i>
                    </button>
                  ))}
                </div>
              ) : null}
              {workResults.length > 0 ? (
                <div>
                  <p className="px-2 py-1.5 font-label text-[11px] tracking-wide text-foreground-400">
                    影视作品
                  </p>
                  {workResults.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => go(`/detail/work/${item.id}`)}
                      className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-left transition-colors hover:bg-primary-50"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-800">
                        <i className="ri-film-line text-sm" aria-hidden="true"></i>
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-label text-sm text-foreground-900">{item.title}</span>
                        <span className="block truncate font-label text-[11px] text-foreground-500">
                          {item.type} · {item.year}
                        </span>
                      </span>
                      <i className="ri-arrow-right-s-line text-foreground-300" aria-hidden="true"></i>
                    </button>
                  ))}
                </div>
              ) : null}
            </>
          ) : (
            <div className="px-3 py-6 text-center">
              <p className="font-label text-sm text-foreground-700">没有找到匹配的「{query}」</p>
              <button
                type="button"
                onClick={() => go("/category")}
                className="mt-2 font-label text-xs text-primary-700 underline underline-offset-2"
              >
                前往女优分类页浏览全部档案
              </button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}